/**
 * Whether a ticket is any good today.
 *
 * Derived on read and never stored. A boolean column called `isCurrent` is wrong
 * by lunchtime on the day a licence lapses, and the version of that bug that
 * matters is the one where a technician walks onto a commercial site with an
 * expired licence because a nightly job had not run yet.
 *
 * Shared so that the job card, the settings screen and any future compliance
 * report all agree on what "due" means, and so the boundary cases have one set
 * of tests rather than three.
 */

export type LicenceStandingKey = 'held' | 'due' | 'expired' | 'missing' | 'undated'

export type LicenceRequirement = {
  /** How long before expiry the ticket starts reading as due. */
  warnDays: number
  /** False for an internal sign-off, which is dated and does not lapse. */
  expires: boolean
}

export type LicenceHolding = {
  expiresAt: Date | string | null
} | null

export type LicenceStanding = {
  key: LicenceStandingKey
  /** Days until expiry. Negative once it has lapsed, null where it cannot. */
  daysLeft: number | null
}

const DAY = 24 * 60 * 60 * 1000

/**
 * Days between two instants, counted in whole calendar days rather than
 * 24-hour blocks. A licence that expires at midnight tonight has one day left
 * for the person reading the screen, not zero-point-three.
 */
function daysBetween(from: Date, to: Date): number {
  const a = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate())
  const b = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate())
  return Math.round((b - a) / DAY)
}

export function licenceStanding(
  requirement: LicenceRequirement,
  holding: LicenceHolding,
  now: Date = new Date(),
): LicenceStanding {
  if (!holding) return { key: 'missing', daysLeft: null }

  // An internal sign-off has a date and no expiry. Holding it is the whole
  // question, so stop here rather than reading a null expiry as a problem.
  if (!requirement.expires) return { key: 'held', daysLeft: null }

  if (holding.expiresAt === null) {
    // A state licence with no expiry recorded is a gap in the record, not a
    // licence that never expires. Naming it that way is the only way it ever
    // gets filled in.
    return { key: 'undated', daysLeft: null }
  }

  const expiresAt = holding.expiresAt instanceof Date ? holding.expiresAt : new Date(holding.expiresAt)
  const daysLeft = daysBetween(now, expiresAt)

  if (daysLeft < 0) return { key: 'expired', daysLeft }
  if (daysLeft <= requirement.warnDays) return { key: 'due', daysLeft }
  return { key: 'held', daysLeft }
}

/**
 * Who prints the technician card.
 *
 * Every instrument from this authority in the catalogue is an endorsement on
 * the one photo card a technician carries, so they share its number, its
 * expiry and its photograph. The business's own licence is not a
 * `LicenceType` at all. Provisional: the licence table ships empty until the
 * business confirms its WA licences (ADR 005), so nothing carries it yet.
 */
export const TECHNICIAN_CARD_AUTHORITY = 'Department of Health WA'

/** True where this ticket is an endorsement on the technician card. */
export function onTechnicianCard(type: { authority: string | null }): boolean {
  return type.authority === TECHNICIAN_CARD_AUTHORITY
}

/** True where this standing should stop the work rather than inform it. */
export function blocksWork(standing: LicenceStandingKey, isMandatory: boolean): boolean {
  if (!isMandatory) return false
  return standing === 'missing' || standing === 'expired'
}
